import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  Share2,
  RefreshCw,
  Check,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Zap,
  LayoutTemplate,
  MessageSquare
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init"; 

const SocialMediaPostGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [topic, setTopic] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [tone, setTone] = useState("Professional");
  const [selectedPlatforms, setSelectedPlatforms] = useState({
    instagram: true,
    facebook: true,
    linkedin: true,
    twitter: true,
  });

  // Generated Content State
  const [generatedPosts, setGeneratedPosts] = useState({});

  // Options
  const toneOptions = [
    "Professional",
    "Casual",
    "Friendly",
    "Persuasive",
    "Educational",
    "Witty",
    "Urgent",
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (UNCHANGED)
  // ==========================================

  // Fetch API keys
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) {
          setError("No API keys available. Please contact administrator.");
        }
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch User Data
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/users`, {
          params: { userEmail: user?.email },
            
        });
        console.log("User Response:", userResponse.data);
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          setCredits(userData.currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Helper: Get Random API Key
  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    const activePlatforms = Object.keys(selectedPlatforms).filter(k => selectedPlatforms[k]);
    
    return `You are an expert Social Media Manager. Create high-engagement social media posts for the following platforms: ${activePlatforms.join(", ")}.

    Topic/Content: ${topic}
    Target Audience: ${targetAudience || "General Audience"}
    Tone: ${tone}

    For EACH platform, follow these rules:
    1. **Hook:** Start with a strong, attention-grabbing first line.
    2. **Body:** Use short, readable sentences. Add spacing for readability.
    3. **Tone:** Adapt the content to fit the specific platform (e.g., LinkedIn = Professional/Value, Instagram = Visual/Casual, Twitter = Concise/Punchy).
    4. **CTA:** Include a natural Call to Action.
    5. **Hashtags:** Include 3-5 relevant hashtags at the bottom.

    **CRITICAL OUTPUT FORMAT:**
    You must separate each post with a specific delimiter. 
    Use exactly these headers: 
    ===INSTAGRAM===
    ===FACEBOOK===
    ===LINKEDIN===
    ===TWITTER===
    
    Only generate sections for the requested platforms. Do not add markdown code blocks (like \`\`\`json). Just raw text separated by headers.`;
  };

  const parseResponse = (text) => {
    const results = {};
    const platforms = ['instagram', 'facebook', 'linkedin', 'twitter'];
    
    platforms.forEach(platform => {
      if (selectedPlatforms[platform]) {
        const regex = new RegExp(`===${platform.toUpperCase()}===([\\s\\S]*?)(?=(===[A-Z]+===|$))`, 'i');
        const match = text.match(regex);
        if (match && match[1]) {
          results[platform] = match[1].trim();
        }
      }
    });
    return results;
  };

  const generatePosts = async () => {
    // Validation
    const activeCount = Object.values(selectedPlatforms).filter(Boolean).length;
    if (!topic.trim()) {
      toast.error("Please enter a topic.");
      return;
    }
    if (activeCount === 0) {
      toast.error("Please select at least one platform.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits. You need at least 1 credit.");
      return;
    }

    setGenerating(true);
    setError("");
    setGeneratedPosts({});

    try {
      // 1. Deduct Credit
      const deductResponse = await axios.post(
        `https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: 1,
        }
      );

      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      setCredits((prev) => prev - 1);

      // 2. Call AI
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.8,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048,
          },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (generatedText) {
        const parsed = parseResponse(generatedText);
        setGeneratedPosts(parsed);
        toast.success("Posts generated successfully!");
      } else {
        throw new Error("Empty response from AI");
      }

    } catch (err) {
      console.error("Generation Error:", err);
      toast.error(err.message);
      setError(err.message);

      // Refund Logic
      try {
        await axios.post(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits((prev) => prev + 1);
        toast("Credits refunded due to error.");
      } catch (refundErr) {
        console.error("Refund failed:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS
  // ==========================================

  const togglePlatform = (key) => {
    setSelectedPlatforms(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied!"));
  };

  const downloadText = (text, platform) => {
    const element = document.createElement("a");
    const file = new Blob([text], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `${platform}-post.txt`;
    document.body.appendChild(element);
    element.click();
  };

  const getPlatformIcon = (key, size = 18) => {
    switch(key) {
      case 'instagram': return <Instagram size={size} />;
      case 'facebook': return <Facebook size={size} />;
      case 'linkedin': return <Linkedin size={size} />;
      case 'twitter': return <Twitter size={size} />;
      default: return <Share2 size={size} />;
    }
  };

  const getPlatformColor = (key) => {
    switch(key) {
      case 'instagram': return 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)';
      case 'facebook': return '#1877F2';
      case 'linkedin': return '#0077b5';
      case 'twitter': return '#000000'; // X branding
      default: return '#666';
    }
  };

  // ==========================================
  // 5. RENDER
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-container">
        <div className="spinner"></div>
        <p>Initializing Generator...</p>
        <style jsx>{`
          .loader-container { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; background: #f8fafc; color: #64748b; }
          .spinner { width: 40px; height: 40px; border: 3px solid #cbd5e1; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 1rem; }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wall">
        <div className="auth-card">
          <h2>Log in Required</h2>
          <p>Please log in to access the Social Media Generator.</p>
          <button onClick={() => window.location.href = "/login"}>Go to Login</button>
        </div>
        <style jsx>{`
          .auth-wall { display: flex; align-items: center; justify-content: center; height: 100vh; background: #f1f5f9; }
          .auth-card { background: white; padding: 2rem; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); text-align: center; max-width: 400px; width: 90%; }
          .auth-card h2 { margin-bottom: 1rem; color: #1e293b; }
          .auth-card button { background: #3b82f6; color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
          .auth-card button:hover { background: #2563eb; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="app-container">
   
      
      {/* HEADER */}
      <header className="app-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => window.history.back()}>&larr; Back</button>
          <h1>Social Media Post Generator</h1>
        </div>
        <div className="credits-badge">
          <Zap size={16} fill="#eab308" color="#eab308" />
          <span>{credits} Credits</span>
          {credits < 5 && <button onClick={() => window.location.href = "/user/select-package"}>Buy More</button>}
        </div>
      </header>

      <main className="main-grid">
        {/* LEFT COLUMN: INPUTS */}
        <section className="input-panel">
          
          <div className="panel-card">
            <h3><LayoutTemplate size={18} /> Topic & Context</h3>
            <div className="form-group">
              <label>What is your post about?</label>
              <textarea 
                value={topic} 
                onChange={(e) => setTopic(e.target.value)} 
                placeholder="e.g. Launching our new eco-friendly coffee cups, asking followers about their morning routine..."
                rows={4}
              />
            </div>
            
            <div className="form-group">
              <label>Target Audience (Optional)</label>
              <input 
                type="text" 
                value={targetAudience} 
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder="e.g. Small business owners, Gen Z, Fitness enthusiasts" 
              />
            </div>
          </div>

          <div className="panel-card">
            <h3><Share2 size={18} /> Platforms</h3>
            <div className="platform-grid">
              {['instagram', 'facebook', 'linkedin', 'twitter'].map(platform => (
                <div 
                  key={platform}
                  className={`platform-toggle ${selectedPlatforms[platform] ? 'active' : ''}`}
                  onClick={() => togglePlatform(platform)}
                >
                  <div className="icon-wrapper" style={{ 
                    background: selectedPlatforms[platform] ? getPlatformColor(platform) : '#e2e8f0',
                    color: selectedPlatforms[platform] ? 'white' : '#64748b'
                  }}>
                    {getPlatformIcon(platform)}
                  </div>
                  <span>{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                  {selectedPlatforms[platform] && <div className="check-mark"><Check size={12} /></div>}
                </div>
              ))}
            </div>
          </div>

          <div className="panel-card">
            <h3><MessageSquare size={18} /> Tone & Style</h3>
            <div className="form-group">
              <label>Select Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)}>
                {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <button 
            className="generate-btn" 
            onClick={generatePosts}
            disabled={generating || credits < 1 || !topic.trim()}
          >
            {generating ? (
              <>
                <RefreshCw className="spin" size={20} /> Generating...
              </>
            ) : (
              <>
                <Zap size={20} /> Generate Posts (1 Credit)
              </>
            )}
          </button>
          
          {error && <div className="error-msg">{error}</div>}
        </section>

        {/* RIGHT COLUMN: RESULTS */}
        <section className="results-panel">
          {generating ? (
            <div className="placeholder-state">
              <div className="pulsing-ai">
                <Zap size={40} />
              </div>
              <p>Crafting viral content for your platforms...</p>
            </div>
          ) : Object.keys(generatedPosts).length > 0 ? (
            <div className="results-grid">
              {Object.entries(generatedPosts).map(([platform, content]) => (
                <div key={platform} className="result-card">
                  <div className="card-header" style={{ borderTop: `4px solid ${platform === 'instagram' ? '#cc2366' : platform === 'facebook' ? '#1877F2' : platform === 'linkedin' ? '#0077b5' : '#000'}` }}>
                    <div className="platform-id">
                      {getPlatformIcon(platform)}
                      <span>{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                    </div>
                    <div className="actions">
                      <button onClick={() => copyToClipboard(content)} title="Copy"><Copy size={16} /></button>
                      <button onClick={() => downloadText(content, platform)} title="Download"><Download size={16} /></button>
                    </div>
                  </div>
                  <div className="card-body">
                    <pre>{content}</pre>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="placeholder-state">
              <LayoutTemplate size={48} opacity={0.2} />
              <h3>Ready to Create?</h3>
              <p>Fill in the details on the left to generate optimized posts for multiple platforms instantly.</p>
            </div>
          )}
        </section>
      </main>

      {/* STYLES */}
      <style jsx>{`
        /* --- GLOBAL & LAYOUT --- */
        .app-container {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background-color: #f8fafc;
          min-height: 100vh;
          color: #334155;
          padding-bottom: 40px;
        }

        .app-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 2rem;
          background: white;
          border-bottom: 1px solid #e2e8f0;
          margin-bottom: 2rem;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .header-left h1 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .back-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          font-weight: 500;
        }

        .credits-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #fefce8;
          color: #854d0e;
          padding: 0.5rem 1rem;
          border-radius: 999px;
          border: 1px solid #fef08a;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .credits-badge button {
          background: #eab308;
          color: white;
          border: none;
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          font-size: 0.75rem;
          cursor: pointer;
          margin-left: 0.5rem;
        }

        /* --- MAIN GRID --- */
        .main-grid {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 2rem;
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        @media (max-width: 900px) {
          .main-grid {
            grid-template-columns: 1fr;
          }
        }

        /* --- INPUT PANEL --- */
        .input-panel {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .panel-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }

        .panel-card h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 1rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #475569;
          margin-bottom: 0.5rem;
        }

        .form-group input, 
        .form-group textarea, 
        .form-group select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: border-color 0.2s;
          background: #fff;
        }

        .form-group input:focus, 
        .form-group textarea:focus, 
        .form-group select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* Platform Toggles */
        .platform-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }

        .platform-toggle {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
        }

        .platform-toggle:hover {
          background: #f8fafc;
        }

        .platform-toggle.active {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .icon-wrapper {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .platform-toggle span {
          font-size: 0.875rem;
          font-weight: 500;
        }

        .check-mark {
          position: absolute;
          top: -6px;
          right: -6px;
          background: #3b82f6;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Generate Button */
        .generate-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border: none;
          padding: 1rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: transform 0.1s;
          box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
        }

        .generate-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 8px -1px rgba(37, 99, 235, 0.3);
        }

        .generate-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          background: #94a3b8;
        }

        .error-msg {
          background: #fef2f2;
          color: #ef4444;
          padding: 0.75rem;
          border-radius: 8px;
          font-size: 0.875rem;
          border: 1px solid #fecaca;
        }

        /* --- RESULTS PANEL --- */
        .results-panel {
          display: flex;
          flex-direction: column;
        }

        .placeholder-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 400px;
          background: white;
          border-radius: 12px;
          border: 2px dashed #e2e8f0;
          color: #94a3b8;
          text-align: center;
          padding: 2rem;
        }

        .placeholder-state h3 {
          color: #475569;
          margin-top: 1rem;
        }

        .pulsing-ai {
          color: #3b82f6;
          animation: pulse 1.5s infinite;
        }
        @keyframes pulse { 0% { opacity: 0.5; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.1); } 100% { opacity: 0.5; transform: scale(0.9); } }
        .spin { animation: spin 1s linear infinite; }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .result-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .card-header {
          padding: 1rem;
          background: #fff;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .platform-id {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #1e293b;
        }

        .actions {
          display: flex;
          gap: 0.5rem;
        }

        .actions button {
          background: #f1f5f9;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }

        .actions button:hover {
          background: #e2e8f0;
          color: #334155;
        }

        .card-body {
          padding: 1.5rem;
          flex-grow: 1;
          background: #fafafa;
        }

        .card-body pre {
          white-space: pre-wrap;
          font-family: inherit;
          font-size: 0.95rem;
          line-height: 1.6;
          color: #334155;
          margin: 0;
        }
      `}</style>
    </div>
  );
};

export default SocialMediaPostGenerator;